import React from 'react';
import { SearchIcon, ChevronDownIcon, BellIcon, HomeIcon, PawPrintIcon, FileTextIcon, CreditCardIcon, AlertTriangleIcon, BarChart3Icon } from 'lucide-react';
import { dashCopy, dashNav, type Bi } from '../../../data/animalsIntelligence';
import { PriorityCards, CompliancePanel, StatusPanel, RevenuePanel, RecommendationPanel } from './IntelligencePanels';
type P = {
  isFR: boolean;
};
const pk = (isFR: boolean) => (v: Bi) => isFR ? v.fr : v.en;
// Exact logo files used in the site header (components/Header.tsx)
const BCITI_LOGO = 'https://cdn.magicpatterns.com/uploads/kFQruRR3oauw7NNUdgFajn/Logo_BCITI_(5).png';
const ATNA_LOGO = 'https://cdn.magicpatterns.com/uploads/d6cqpG2SGUofNqC1hEedXv/Logo_ATNA_(5).png';
const NAV_ICONS = {
  dashboard: HomeIcon,
  animals: PawPrintIcon,
  licences: FileTextIcon,
  payments: CreditCardIcon,
  reminders: BellIcon,
  reports: AlertTriangleIcon,
  analytics: BarChart3Icon
} as const;

// Full organization dashboard, framed as a monitor (desktop only).
export function IntelligenceMonitor({
  isFR
}: P) {
  const pick = pk(isFR);
  return <div className="rounded-[28px] bg-[#1B1D26] p-[10px] shadow-[0_60px_100px_-50px_rgba(20,20,60,0.6)]" data-id="element-370" data-mp="2gtk8exxlbi2q">
      <div className="rounded-[20px] bg-[#FCFCFE] overflow-hidden flex" data-id="element-371" data-mp="2vhx4rw0lb2xr">
        {/* Sidebar */}
        <aside className="w-[170px] shrink-0 bg-white border-r border-[#F0F0F6] p-3" data-id="element-372" data-mp="2qjc2zs5owb77">
          <div className="flex items-center gap-1.5 px-1.5 pt-1 pb-4" data-id="element-373" data-mp="22thwrtfvlkvx">
            <img src={BCITI_LOGO} alt="BCITI" className="h-6 w-auto object-contain" data-id="element-374" data-mp="22trj971pp9wv" />
            <span className="text-[#121212]/25 font-sans text-sm font-light select-none" aria-hidden="true" data-id="element-375" data-mp="1xvzjjqutsji4">|</span>
            <img src={ATNA_LOGO} alt="ATNA" className="h-5 w-auto object-contain" data-id="element-376" data-mp="2bs37x3j0pxmo" />
          </div>
          <ul className="space-y-1" data-id="element-377" data-mp="27up27lujq7zf">
            {dashNav.map(n => {
            const Icon = NAV_ICONS[n.key];
            const active = n.key === 'dashboard';
            return <li key={n.key} className={`flex items-center gap-2 rounded-lg px-2.5 py-2 font-sans text-[12px] whitespace-nowrap ${active ? 'bg-[#EEEDFD] text-[#4F46E5] font-semibold' : 'text-[#121A3A]'}`} data-id="element-378" data-mp="3j7hoih4ki483">
                  <Icon className={`w-3.5 h-3.5 shrink-0 ${active ? 'text-[#4F46E5]' : 'text-[#6B7385]'}`} strokeWidth={1.8} aria-hidden="true" data-id="element-379" data-mp="2psx6jeqe0e6k" />
                  {pick(n.label)}
                </li>;
          })}
          </ul>
        </aside>

        {/* Main */}
        <div className="flex-1 min-w-0 p-4" data-id="element-380" data-mp="225yt6pxkdmcv">
          <div className="flex items-center gap-3" data-id="element-381" data-mp="26726p5s536ax">
            <div className="flex-1 min-w-0 flex items-center gap-2 rounded-lg border border-[#ECECF4] bg-white px-3 py-2" data-id="element-382" data-mp="265hfnq51a5ht">
              <SearchIcon className="w-3.5 h-3.5 text-[#8A90A2] shrink-0" aria-hidden="true" data-id="element-383" data-mp="2azvb1sjp4cmh" />
              <span className="font-sans text-[11.5px] text-[#8A90A2] truncate" data-id="element-384" data-mp="1eds1i42sa3js">{pick(dashCopy.search)}</span>
            </div>
            <span className="flex items-center gap-1.5 rounded-lg border border-[#ECECF4] bg-white px-3 py-2 font-sans text-[11.5px] text-[#3A4258] whitespace-nowrap" data-id="element-385" data-mp="1axox7f5nlpzm">
              {pick(dashCopy.territory)}
              <ChevronDownIcon className="w-3 h-3" aria-hidden="true" data-id="element-386" data-mp="11wr1dk5dcos4" />
            </span>
            <BellIcon className="w-4 h-4 text-[#3A4258] shrink-0" aria-hidden="true" data-id="element-387" data-mp="wyfr58b5wc9d" />
            <span className="w-8 h-8 rounded-full bg-[#121A3A] text-white font-sans text-[11px] font-semibold flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-388" data-mp="rz2trtmnzmsj">
              LG
            </span>
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-2" data-id="element-389" data-mp="i8rni0fid1ih">
            <p className="font-sans text-[19px] font-semibold text-[#121A3A]" data-id="element-390" data-mp="e7y1jclmlws8">{pick(dashCopy.title)}</p>
            <div className="flex items-center gap-3" data-id="element-391" data-mp="98bmcxm8m6gs">
              <span className="flex items-center gap-1.5 font-sans text-[11px] text-[#3A4258]" data-id="element-392" data-mp="97tipfx8wpyu">
                <span className="w-2 h-2 rounded-full bg-[#22A04F]" aria-hidden="true" data-id="element-393" data-mp="4aa6elk3yn9j" />
                {pick(dashCopy.live)}
              </span>
              <span className="flex items-center gap-1.5 rounded-lg border border-[#ECECF4] bg-white px-2.5 py-1.5 font-sans text-[11px] text-[#3A4258]" data-id="element-394" data-mp="29bckjb0fx4fz">
                {pick(dashCopy.period)}
                <ChevronDownIcon className="w-3 h-3" aria-hidden="true" data-id="element-395" data-mp="1zkrptynw0k2c" />
              </span>
            </div>
          </div>

          <div className="mt-3" data-id="element-396" data-mp="1lk73wtmoe84m">
            <PriorityCards isFR={isFR} data-id="element-397" data-mp="1ljyujbprhky6" />
          </div>
          <div className="mt-3 grid grid-cols-3 gap-3" data-id="element-398" data-mp="1pn3r6xn5usew">
            <CompliancePanel isFR={isFR} data-id="element-399" data-mp="1pkipq9qryeho" />
            <StatusPanel isFR={isFR} data-id="element-400" data-mp="1bnvo3tnvn05f" />
            <RevenuePanel isFR={isFR} data-id="element-401" data-mp="1box66i3b8mwn" />
          </div>
          <div className="mt-3" data-id="element-402" data-mp="1gldrz5n1ep6k">
            <RecommendationPanel isFR={isFR} data-id="element-403" data-mp="1gmp2rpewgx76" />
          </div>
        </div>
      </div>
    </div>;
}